import axios from 'axios';

/**
 * Servicio para manejar configuraciones del sistema
 * Comunicación con la API de configuraciones
 */
class ConfigService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    this.endpoint = '/config/system';
  }

  /**
   * Obtener token de autenticación
   */
  getAuthToken() {
    return localStorage.getItem('adminToken');
  }

  /**
   * Configurar headers de autenticación
   */
  getAuthHeaders() {
    const token = this.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Obtener todas las configuraciones del sistema
   */
  async getConfigs(params = {}) {
    try {
      const response = await axios.get(`${this.baseURL}${this.endpoint}`, {
        headers: this.getAuthHeaders(),
        params
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al obtener configuraciones'));
    }
  }

  /**
   * Obtener configuraciones activas
   */
  async getActiveConfigs() {
    try {
      const response = await axios.get(`${this.baseURL}${this.endpoint}/active`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al obtener configuraciones activas'));
    }
  }

  /**
   * Buscar configuraciones por clave
   */
  async searchConfigsByKey(key) {
    try {
      const response = await axios.get(`${this.baseURL}${this.endpoint}/search`, {
        headers: this.getAuthHeaders(),
        params: { key }
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al buscar configuraciones'));
    }
  }

  /**
   * Obtener configuración por ID
   */
  async getConfigById(id) {
    try {
      const response = await axios.get(`${this.baseURL}${this.endpoint}/${id}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al obtener configuración'));
    }
  }

  /**
   * Obtener configuración por clave
   */
  async getConfigByKey(key) {
    try {
      const response = await axios.get(`${this.baseURL}${this.endpoint}/key/${key}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al obtener configuración por clave'));
    }
  }

  /**
   * Crear nueva configuración
   */
  async createConfig(configData) {
    try {
      const response = await axios.post(`${this.baseURL}${this.endpoint}`, configData, {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al crear configuración'));
    }
  }

  /**
   * Actualizar configuración por ID
   */
  async updateConfig(id, configData) {
    try {
      const response = await axios.patch(`${this.baseURL}${this.endpoint}/${id}`, configData, {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al actualizar configuración'));
    }
  }

  /**
   * Actualizar configuración por clave
   */
  async updateConfigByKey(key, value) {
    try {
      const response = await axios.patch(`${this.baseURL}${this.endpoint}/key/${key}`, { value }, {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al actualizar configuración por clave'));
    }
  }

  /**
   * Cambiar estado activo/inactivo de configuración
   */
  async toggleConfigActive(id) {
    try {
      const response = await axios.patch(`${this.baseURL}${this.endpoint}/${id}/toggle-active`, {}, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al cambiar estado de configuración'));
    }
  }

  /**
   * Eliminar configuración
   */
  async deleteConfig(id) {
    try {
      await axios.delete(`${this.baseURL}${this.endpoint}/${id}`, {
        headers: this.getAuthHeaders()
      });
      return true;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al eliminar configuración'));
    }
  }

  /**
   * Obtener configuraciones públicas del sistema
   */
  async getPublicConfigs() {
    try {
      const response = await axios.get(`${this.baseURL}/config`);
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al obtener configuraciones públicas'));
    }
  }

  /**
   * Obtener configuraciones de autenticación
   */
  async getAuthConfigs() {
    try {
      const response = await axios.get(`${this.baseURL}/config/auth`);
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, 'Error al obtener configuraciones de autenticación'));
    }
  }

  /**
   * Procesar mensajes de error
   */
  getErrorMessage(error, defaultMessage) {
    if (error.response) {
      // Error de respuesta del servidor
      const { status, data } = error.response;
      
      if (status === 401) {
        return 'No tienes permisos para realizar esta acción';
      }
      
      if (status === 403) {
        return 'Acceso denegado. Se requiere rol de administrador';
      }
      
      if (status === 404) {
        return 'Configuración no encontrada';
      }
      
      if (status === 409) {
        return data?.message || 'Ya existe una configuración con esta clave';
      }
      
      if (status === 422) {
        return data?.message || 'Datos de configuración inválidos';
      }
      
      if (data?.message) {
        return data.message;
      }
      
      return `Error del servidor (${status})`;
    }
    
    if (error.request) {
      // Error de red
      return 'Error de conexión. Verifica tu conexión a internet';
    }
    
    // Error general
    return error.message || defaultMessage;
  }
}

// Exportar instancia única
const configServiceInstance = new ConfigService();
export default configServiceInstance; 